import SelectCurrency from "../../../../components/Currencies/SelectCurrency";
import React, { useCallback, useEffect, useState, useMemo } from "react";
import { Button } from "../../../../components/Button/Button";
import { Link } from "react-router-dom";
import moment from "moment";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import { TextInput } from "../../../../components/TextInput/TextInput";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { toast } from "react-hot-toast";
import backArrow from "../../../../assets/backarrow.svg";
import { Controller } from "react-hook-form";
import { http } from "../../../../utils/httpCommon";
import { PasswordInput } from "@mantine/core";
import { prettyNumber } from "../../../../lib/prettyNumber";
import { Switch, Checkbox } from "@mui/material";
import CalculatedCurrencyAmount from "../../../../components/Currencies/CalculatedCurrencyAmount";

const CalendarFormStep2 = ({
  responseData,
  close,
  paymentMethods,
  fetchAppointmentsData,
  subTotal,
  vat,
  total,
  wht,
  amountPaid,
  discount,
  promoCodeAmount,
  dayId = null,  // Optional: specific day to pay for
}) => {
  const [steps, setSteps] = useState(0);
  const [payWithCredit, setPayWithCredit] = useState(false);
  const [isCompany, setIsCompany] = useState(false);
  const [currencySymbol, setCurrencySymbol] = useState("EGP");
  const [selectedDayIds, setSelectedDayIds] = useState(() => dayId ? [dayId] : []);
  
  // Update selectedDayIds when dayId prop changes
  useEffect(() => {
    if (dayId) {
      setSelectedDayIds([dayId]);
    } else if (responseData?.days?.length === 1) {
      // If only one day, auto-select it
      setSelectedDayIds([responseData.days[0]._id]);
    }
  }, [dayId, responseData?.days]);

  // const paidAmountValidation = useCallback(() => {
  //   if (payWithCredit) {
  //     return yup.string().optional();
  //   } else {
  //     return yup.string().typeError("Amount paid is required").required("Amount paid is required");
  //   }
  // }, [payWithCredit]);

  const paymentMethodIdValidation = useCallback(() => {
    if (payWithCredit) {
      return yup.string().optional();
    } else {
      return yup
        .string()
        .typeError("Payment method is required")
        .required("Payment method is required");
    }
  }, [payWithCredit]);

  const schema = yup
    .object({
      paidAmount: yup
        .string()
        .typeError("Amount paid is required")
        .required("Amount paid is required"),
      adminPassword:
        steps === 0
          ? yup.string()
          : yup
              .string()
              .typeError("Admin password is required")
              .required("Admin password is required"),
      paymentMethodId: paymentMethodIdValidation(),
      currency: yup.string().required("Currency is required"),
      invoice: yup.boolean(),
      vat: yup.boolean(),
      wht: yup.boolean(),
    })
    .required();
  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    control,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      currency: responseData?.user?.preferredCurrency || "",
    },
  });

  useEffect(() => {
    // paidAmountValidation();
    paymentMethodIdValidation();
    watch("paymentMethodId");
    watch("paidAmount");
  }, [
    payWithCredit,
    watch,
    // paidAmountValidation,
    paymentMethodIdValidation,
  ]);
  const currency = watch("currency");

  // Calculate per-day financials (similar to BookingDetails)
  const dayFinancials = useMemo(() => {
    const result = {};
    const days = responseData?.days || [];
    const bookingTotal = Number(responseData?.totalAmount) || 0;
    const bookingSubTotal = Number(responseData?.subTotal) || 0;
    const totalDuration = days.reduce((sum, day) => sum + (day.endTime - day.startTime), 0) || 0;

    let accumulatedTotal = 0;
    let accumulatedSubTotal = 0;

    days.forEach((day, index) => {
      const isLastDay = index === days.length - 1;
      const ratio = totalDuration > 0 ? (day.endTime - day.startTime) / totalDuration : 0;

      let dayTotal;
      let daySubtotal;

      if (isLastDay) {
        dayTotal = bookingTotal - accumulatedTotal;
        daySubtotal = bookingSubTotal - accumulatedSubTotal;
      } else {
        dayTotal = Math.round(bookingTotal * ratio);
        daySubtotal = Math.round(bookingSubTotal * ratio);
        accumulatedTotal += dayTotal;
        accumulatedSubTotal += daySubtotal;
      }

      const dayVat = dayTotal - daySubtotal;

      result[day._id] = {
        total: dayTotal,
        subtotal: daySubtotal,
        vat: dayVat,
      };
    });

    return result;
  }, [responseData?.days, responseData?.totalAmount, responseData?.subTotal]);

  // Calculate paid per day - simply read from day.paidAmount (new system)
  const calculatePaidPerDay = useMemo(() => {
    const paidPerDay = {};
    const allDays = responseData?.days || [];
    
    // Simply read the stored paidAmount from each day
    allDays.forEach((day) => {
      paidPerDay[day._id] = Number(day.paidAmount) || 0;
    });
    
    return paidPerDay;
  }, [responseData?.days]);

  // Calculate total for selected days
  const selectedDaysTotal = useMemo(() => {
    // If dayId is provided (single day payment), use that
    if (dayId) {
      const dayTotal = dayFinancials[dayId]?.total || 0;
      const dayPaidRaw = calculatePaidPerDay[dayId] || 0;
      // Cap paid at dayTotal to avoid negative remaining (refunds handled separately)
      const dayPaid = Math.min(dayPaidRaw, dayTotal);
      return Math.max(0, dayTotal - dayPaid);
    }
    // If no days selected, return total (all remaining)
    if (selectedDayIds.length === 0) return total;
    // If single day selected
    if (selectedDayIds.length === 1) {
      const selectedDayId = selectedDayIds[0];
      const dayTotal = dayFinancials[selectedDayId]?.total || 0;
      const dayPaidRaw = calculatePaidPerDay[selectedDayId] || 0;
      // Cap paid at dayTotal to avoid negative remaining (refunds handled separately)
      const dayPaid = Math.min(dayPaidRaw, dayTotal);
      return Math.max(0, dayTotal - dayPaid);
    }
    // Multiple days selected
    return selectedDayIds.reduce((sum, selectedDayId) => {
      const dayTotal = dayFinancials[selectedDayId]?.total || 0;
      const dayPaidRaw = calculatePaidPerDay[selectedDayId] || 0;
      // Cap paid at dayTotal to avoid negative remaining (refunds handled separately)
      const dayPaid = Math.min(dayPaidRaw, dayTotal);
      return sum + Math.max(0, dayTotal - dayPaid);
    }, 0);
  }, [selectedDayIds, dayFinancials, calculatePaidPerDay, total, dayId]);

  // Pre-fill paidAmount with the remaining amount for selected day(s)
  // This ensures when user clicks "Pay Day", the correct amount is pre-filled
  useEffect(() => {
    if (selectedDaysTotal > 0) {
      setValue("paidAmount", String(selectedDaysTotal));
    }
  }, [selectedDaysTotal, setValue]);

  const [submitLoading, setSubmitLoading] = useState(false);
  const onSubmit = async (passedData) => {
    if (steps === 0) {
      setSteps(1);
    } else {
      setSubmitLoading(true);
      try {
        // If single day selected, pass dayId; otherwise null (general payment)
        const paymentDayId = selectedDayIds.length === 1 ? selectedDayIds[0] : null;
        
        await http.put(
          `bookings/${responseData?._id}/pay`,
          payWithCredit
            ? JSON.stringify({
                adminPassword: passedData.adminPassword,
                promoCode: passedData?.promoCode,
                payUsingCredit: payWithCredit,
                useCompanyCredit: isCompany,
                paidAmount: passedData?.paidAmount,
                currencyId: passedData?.currency,
                dayId: paymentDayId,
              })
            : JSON.stringify({
                adminPassword: passedData.adminPassword,
                paidAmount: passedData?.paidAmount,
                paymentMethodId: passedData?.paymentMethodId,
                promoCode: passedData?.promoCode,
                payUsingCredit: payWithCredit,
                currencyId: passedData?.currency,
                dayId: paymentDayId,
              })
        );

        fetchAppointmentsData();
        toast.success("Booking payment created successfully");
        close();
      } catch (error) {
        toast.error(
          error?.response?.data?.message || error?.response?.message || "Something went wrong"
        );
      } finally {
        setSubmitLoading(false);
      }
    }
  };

  return (
    <>
      {steps === 0 ? (
        <div className="px-4 pb-4 ">
          <div className="flex justify-between ">
            <div
              className="text-[32px] "
              style={{
                fontFamily: "gilroy-bold",
              }}
            >
              Confirm Booking
            </div>
          </div>

          <div className="my-2 flex flex-col gap-3">
            <div className="text-[16px] text-[#797979] font-semibold">Facility</div>
            <div className="border-[#E4E4E4] border-[1px] rounded-[10px] p-[6px] flex gap-3">
              <img
                src={responseData?.facility?.mainImage}
                alt={responseData?.facility?.name}
                className="rounded-[10px] w-[100px] object-cover h-[75px] "
              />
              <div className="flex flex-col justify-between w-full  ">
                <div className="text-[18px] " style={{ fontFamily: "gilroy-bold" }}>
                  {responseData?.facility?.name}
                </div>

                <Link
                  to={`/inventory/facility/${responseData?.facility?._id}`}
                  className="text-[16px] text-[#34B2C0] underline"
                >
                  {" "}
                  Quick view
                </Link>
              </div>
            </div>
          </div>

          <div className="my-2 flex flex-col gap-3">
            <div className="text-[#797979] text-[16px] font-semibold">Client information</div>
            <Link
              to={`/community/client/${responseData?.user?._id}`}
              className="border-[#E4E4E4] border-[1px] rounded-[10px] p-[6px] flex gap-3"
            >
              {responseData?.user?.profilePicture && (
                <img
                  src={responseData?.user?.profilePicture}
                  alt={responseData?.user?.name}
                  className="rounded-[10px] w-[65px] object-cover h-[65px] "
                />
              )}
              <div className="flex flex-col justify-center w-full  ">
                <div className="text-[16px] " style={{ fontFamily: "gilroy-bold" }}>
                  {responseData?.user?.name}
                </div>
                {responseData?.user?.company?.name ? (
                  <div className="text-[#797979]">
                    Member at " {responseData?.user?.company?.name}"
                  </div>
                ) : (
                  <div className="text-[#797979] text-sm">No company</div>
                )}
              </div>
            </Link>
          </div>

          <div className="my-2 flex flex-col gap-3">
            <div className="text-[#797979] text-[16px] font-semibold">Bookings</div>

            <div className="border-[#E4E4E4] border-[1px] rounded-[10px] px-4 py-2 flex flex-col gap-2">
              {responseData?.days?.length > 1 && !dayId ? (
                // Multiple days: show selection checkboxes
                <div className="flex flex-col gap-2">
                  <div className="text-[14px] font-semibold mb-2">Select days to pay for:</div>
                  {responseData?.days?.map((d) => {
                    const dayTotal = dayFinancials[d._id]?.total || 0;
                    const dayPaidRaw = calculatePaidPerDay[d._id] || 0;
                    // Cap paid amount at dayTotal to avoid overpaid display (refunds shown separately)
                    const dayPaid = Math.min(dayPaidRaw, dayTotal);
                    const dayRemaining = Math.max(0, dayTotal - dayPaid);
                    const isSelected = selectedDayIds.includes(d._id);
                    const isFullyPaid = dayRemaining <= 0;
                    
                    return (
                      <div key={d._id} className="flex items-center justify-between p-2 border rounded">
                        <div className="flex items-center gap-2">
                          <Checkbox
                            checked={isSelected}
                            disabled={isFullyPaid}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedDayIds([...selectedDayIds, d._id]);
                              } else {
                                setSelectedDayIds(selectedDayIds.filter(id => id !== d._id));
                              }
                            }}
                          />
                          <div>
                            <div className="text-[14px] font-semibold">
                              {moment(d?.date).format("dddd")} {moment(d?.date).format("LL.")}{" "}
                              {moment(d?.startTime * 1000).format("LT")}
                              {"  -  "}
                              {moment(d?.endTime * 1000).format("LT")}
                            </div>
                            <div className="text-[12px] text-[#797979]">
                              {isFullyPaid ? (
                                <span className="text-green-600">Fully paid</span>
                              ) : (
                                <>Remaining: {prettyNumber(dayRemaining)} EGP</>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="text-[14px] font-semibold">
                          {prettyNumber(dayTotal)} EGP
                        </div>
                      </div>
                    );
                  })}
                  {selectedDayIds.length === 0 && (
                    <div className="text-sm text-orange-600 mt-2">
                      Please select at least one day to pay for
                    </div>
                  )}
                </div>
              ) : (
                // Single day or all days: show list
                <>
                  <div className="text-[14px] " style={{ fontFamily: "gilroy-bold" }}>
                    {responseData?.days?.length}{" "}
                    {responseData?.days?.length > 1 ? "Days between" : "Day between"}{" "}
                    {moment(responseData?.days[responseData?.days?.length - 1]?.date).format("ddd")}{" "}
                    {moment(responseData?.days[responseData?.days?.length - 1]?.date).format(
                      "Do MMM  YYYY"
                    )}{" "}
                    to {moment(responseData?.days?.map((d) => d?.startTime)[0] * 1000).format("ddd")}{" "}
                    {moment(responseData?.days?.map((d) => d?.startTime)[0] * 1000).format(
                      "Do MMM  YYYY"
                    )}{" "}
                  </div>
                  <div className="px-4 py-2 text-[14px]">
                    <ul className="list-disc">
                      {responseData?.days?.map((d) => {
                        const dayTotal = dayFinancials[d._id]?.total || 0;
                        const dayPaidRaw = calculatePaidPerDay[d._id] || 0;
                        // Cap paid amount at dayTotal to avoid overpaid display (refunds shown separately)
                        const dayPaid = Math.min(dayPaidRaw, dayTotal);
                        const dayRemaining = Math.max(0, dayTotal - dayPaid);
                        return (
                          <li key={d?._id}>
                            {moment(d?.date).format("dddd")} {moment(d?.date).format("LL.")}{" "}
                            {moment(d?.startTime * 1000).format("LT")}
                            {"  -  "}
                            {moment(d?.endTime * 1000).format("LT")}
                            {dayId === d._id && (
                              <span className="ml-2 text-blue-600 font-semibold">
                                (Selected: {prettyNumber(dayRemaining)} EGP remaining)
                              </span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </>
              )}
            </div>
          </div>

          <form className="w-full " onSubmit={handleSubmit(onSubmit)}>
            <div className="text-[20px]  mt-5 mb-2 " style={{ fontFamily: "gilroy-bold" }}>
              Payment
            </div>
            {!responseData?.receivable && (
              <>
                <div className="flex justify-between items-center">
                  <div className="w-full  my-2 bg-white py-1  text-[18px]  font-bold ">
                    Pay with Credit
                  </div>
                  <Switch
                    checked={payWithCredit}
                    onChange={(_, checked) => setPayWithCredit(checked)}
                  />
                </div>

                {payWithCredit && (
                  <div className="flex items-center gap-4 justify-center w-full">
                    <span>Personal</span>
                    <Switch checked={isCompany} onChange={() => setIsCompany(!isCompany)} />
                    <span>Company</span>
                  </div>
                )}
              </>
            )}

            {!payWithCredit && !responseData?.receivable && (
              <div className="mt-4 mb-2 grid grid-cols-3 gap-2">
                <div className="col-span-2">
                  <FormControl fullWidth sx={{ border: "none !important" }}>
                    <InputLabel>Payment Method</InputLabel>
                    <Controller
                      control={control}
                      name="paymentMethodId"
                      render={({ field: { onChange, onBlur, value, ref } }) => (
                        <Select
                          sx={{
                            color: "black",
                            ".MuiOutlinedInput-notchedOutline": {
                              borderColor: "#EFEFEF",
                            },
                          }}
                          onBlur={onBlur}
                          onChange={onChange}
                          variant="outlined"
                          labelId="Payment Method-label"
                          id="Payment Method"
                          value={value}
                          label="Payment Method"
                          inputRef={ref}
                          defaultValue=""
                        >
                          <MenuItem value="">
                            <em>None</em>
                          </MenuItem>
                          {paymentMethods?.map((l) => {
                            return (
                              l?.name.toLowerCase() != "receivable" && (
                                <MenuItem key={l?._id} value={l?._id}>
                                  {l?.name}
                                </MenuItem>
                              )
                            );
                          })}
                        </Select>
                      )}
                    />
                  </FormControl>
                  <p className="text-red-500">{errors.paymentMethodId?.message}</p>
                </div>
                <div className="col-span-1">
                  <FormControl fullWidth sx={{ border: "none !important" }}>
                    <InputLabel>Currency</InputLabel>
                    <Controller
                      control={control}
                      defaultValue={responseData?.user?.preferredCurrency}
                      name="currency"
                      render={({ field: { onChange, value, defaultValue } }) => (
                        <SelectCurrency
                          value={value}
                          onChange={onChange}
                          defaultValue={defaultValue}
                          preferredCurrency={responseData?.user?.preferredCurrency}
                        />
                      )}
                    />
                  </FormControl>

                  <p className="text-red-500">{errors.currency?.message}</p>
                </div>
              </div>
            )}
            {/* <div className="flex gap-6  mt-3">
              <div className="flex gap-[5px] items-center">
                <input
                  type="checkbox"
                  name="needInvoice"
                  id="needInvoice"
                  {...register("invoice")}
                />
                <label htmlFor="needInvoice" className="text-[16px]">
                  Needs Invoice
                </label>
                <div className="invalid-feedback"></div>
              </div>

              <div className="flex gap-[5px] items-center">
                <input type="checkbox" name="vat" id="vat" {...register("vat")} />
                <label htmlFor="vat" className="text-[16px]">
                  Exempt from VAT
                </label>
              </div>

              <div className="flex gap-[5px] items-center">
                <input type="checkbox" name="wht" id="wht" {...register("wht")} />
                <label htmlFor="needInvoice" className="text-[16px]">
                  Apply WHT
                </label>
              </div>
            </div> */}

            <div className="border-[1px]   rounded-[10px]  mt-6 px-4 py-2">
              <div className="text-[16px] " style={{ fontFamily: "gilroy-bold" }}>
                Total Due
              </div>
              <div
                className="mt-4"
                style={{
                  display: "grid",
                  gridTemplateColumns: "auto auto auto",
                  gap: "8px",
                  alignItems: "center",
                }}
              >
                <span className=" font-extrabold text-[14px]">Subtotal</span>
                <div className="flex items-center">
                  <span className="text-[#797979]   mx-[10px] ">{prettyNumber(subTotal)} EGP</span>
                </div>
                <div></div>

                {discount && !isNaN(discount) && Number(discount) > 0 ? (
                  <>
                    <div className="font-extrabold">Discount</div>
                    <div className="flex items-center">
                      <span className="text-[#797979] mx-[10px]">
                        -{prettyNumber(discount)} EGP
                      </span>
                    </div>
                    <div></div>
                  </>
                ) : null}

                {wht ? (
                  <>
                    <div className="font-extrabold">WHT</div>
                    <div className="flex items-center">
                      <span className="text-[#797979] mx-[10px]">{prettyNumber(wht)} EGP</span>
                    </div>
                    <div></div>
                  </>
                ) : null}

                {promoCodeAmount && !isNaN(promoCodeAmount) && Number(promoCodeAmount) > 0 ? (
                  <>
                    <div className="font-extrabold">Promo Code</div>
                    <div className="flex items-center">
                      <span className="text-[#797979] mx-[10px]">
                        -{prettyNumber(promoCodeAmount)} EGP
                      </span>
                    </div>
                    <div></div>
                  </>
                ) : null}

                <span className="font-extrabold">VAT</span>
                <div className="flex items-center">
                  <span className="text-[#797979] mx-[10px] ">{prettyNumber(vat)} EGP</span>
                </div>
                <div></div>

                {amountPaid && !isNaN(amountPaid) && Number(amountPaid) > 0 ? (
                  <>
                    <div className="font-extrabold">Amount Paid</div>
                    <div className="flex items-center">
                      <span className="text-[#797979] mx-[10px]">{prettyNumber(amountPaid)}</span>
                    </div>
                    <div></div>
                  </>
                ) : null}
              </div>

              <div className="mt-[15px]">
                <span className="text-[16px] font-semibold">
                  {selectedDayIds.length > 0 && responseData?.days?.length > 1
                    ? `Total for Selected Days`
                    : `Total`}
                </span>
                <br />
                <span
                  className="text-[32px] flex items-center gap-2 "
                  style={{ fontFamily: "gilroy-bold" }}
                >
                  {prettyNumber(selectedDaysTotal)} EGP
                  <CalculatedCurrencyAmount
                    amount={selectedDaysTotal}
                    toCurrency={currency}
                    setCurrencySymbol={setCurrencySymbol}
                  />
                </span>
              </div>
            </div>
            {/* {!payWithCredit && ( */}
            {!responseData?.receivable && (
              <div className="mt-5 ">
                <TextInput
                  label={`Paid Amount in (${currencySymbol})`}
                  type="number"
                  defaultValue={0}
                  {...register("paidAmount")}
                />
                <p className="text-red-500">{errors.paidAmount?.message}</p>
              </div>
            )}
            {/* )} */}

            <div className="flex w-full justify-end  mt-4 gap-5">
              {/* {!isNewAppointment ? ( */}
              <Button
                secondary={true}
                invert={true}
                onClick={() => {
                  fetchAppointmentsData();
                  setTimeout(() => {
                    close();
                  }, 1000);
                }}
              >
                {responseData?.receivable ? "Close" : "Cancel"}
              </Button>

              {!responseData?.receivable && (
                <Button 
                  type="submit" 
                  secondary={true}
                  disabled={responseData?.days?.length > 1 && !dayId && selectedDayIds.length === 0}
                >
                  Pay now
                </Button>
              )}
            </div>
          </form>
        </div>
      ) : (
        <div className="px-10 relative">
          <div
            onClick={() => setSteps(0)}
            className="cursor-pointer flex justify-center items-center w-[40px] aspect-square rounded-full bg-[#EFEFEF] hover:[##686868] "
          >
            <img src={backArrow} alt="back" className="w-[8px] h-[15px] object-contain" />
          </div>
          <div className="flex justify-between ">
            <div
              className="text-[38px] "
              style={{
                fontFamily: "gilroy-bold",
              }}
            >
              Security Confirmation
            </div>
          </div>

          <div className="my-2 flex flex-col gap-3">
            <div className="text-[#797979] text-[16px] font-semibold">Client information</div>
            <Link
              to={`/community/client/${responseData?.user?._id}`}
              className="border-[#E4E4E4] border-[1px] rounded-[10px] p-[5px] flex gap-2"
            >
              <img
                src={responseData?.user?.profilePicture}
                alt={responseData?.user?.name}
                className="rounded-[10px] w-[65px] object-cover h-[65px] "
              />
              <div className="flex flex-col justify-center w-full  ">
                <div className="text-[18px] " style={{ fontFamily: "gilroy-bold" }}>
                  {responseData?.user?.name}
                </div>
                {responseData?.user?.company && (
                  <div className="text-[#797979]">
                    Member at " {responseData?.user?.company?.name}"
                  </div>
                )}
              </div>
            </Link>
          </div>

          <div className="text-[16px]">
            I Admin confirm the payment of "{prettyNumber(+getValues().paidAmount)} {currencySymbol}
            " by {responseData?.user?.name} for Booking facility at {responseData?.location?.name}{" "}
            branch for {responseData?.days?.length} days
          </div>

          <form className="w-full " onSubmit={handleSubmit(onSubmit)}>
            <div className="mt-10 ">
              <PasswordInput
                required
                size="lg"
                radius={"md"}
                placeholder="Admin Password"
                label={`Admin Password `}
                {...register("adminPassword")}
              />
              <p className="text-red-500">{errors.adminPassword?.message}</p>
            </div>

            <div className="flex w-full justify-end mt-4 gap-5">
              <Button
                secondary={true}
                invert={true}
                onClick={() => {
                  close();
                  fetchAppointmentsData();
                }}
              >
                Cancel
              </Button>

              <Button loading={submitLoading} type="submit" secondary={true}>
                Confirm
              </Button>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default CalendarFormStep2;
